import apiService from "@/core/services/apiService";

export type State = { classes: object };
import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import { Actions, Mutations } from "@/store/enums/store.enums";
import { FilemanagerActions, FilemanagerMutation } from "@/modules/filemanager/store/enums";
import store from "@/store";

@Module
export default class Store extends VuexModule {
  renderKey = 1;

  listParams = {
    page: 1,
    results: 10,
    search: null,
    sort: [],
    filter: {}
  };

  listCache = [];
  listTotal = 0;
  listFilterOptions = {};

  get getFilemanagerRenderKey() {
    return "filemanager-" + this.renderKey;
  }

  get getFilemanagerLastListResult() {
    return this.listCache;
  }

  get getFilemanagerLastListTotal() {
    return this.listTotal;
  }

  get getFilemanagerLastFilterOptions() {
    return this.listFilterOptions;
  }

  @Mutation
  [FilemanagerMutation.SET_RENDER_UPDATE]() {
    this.renderKey++;
  }

  @Mutation
  [FilemanagerMutation.SET_LIST_RESULT]({ listParams, listTotal, listCache, listFilterOptions }) {
    this.listParams = listParams;
    this.listCache = listCache;
    this.listTotal = listTotal;
    this.listFilterOptions = listFilterOptions;
  }

  @Action
  [FilemanagerActions.REFRESH_LIST]() {
    this.context.dispatch(FilemanagerActions.FETCH_LIST, this.listParams);
  }

  @Action
  [FilemanagerActions.FETCH_LIST](params) {

    return new Promise<void>((resolve, reject) => {

      apiService.post("filemanager/list", params).then((response) => {

        const data = response.data || null;

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // return results into storage for reactive update if needed //
        this.context.commit(FilemanagerMutation.SET_LIST_RESULT, {
          listParams: params,
          listTotal: data.results,
          listCache: data.result,
          listFilterOptions: data.filter
        });
        resolve();

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [FilemanagerActions.GET_ITEM](detailId) {

    return new Promise<void>((resolve, reject) => {

      apiService.get(`filemanager/get/${detailId}`).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // return results to dispatcher without mutation //
        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [FilemanagerActions.UPDATE_ITEM](payload) {

    return new Promise((resolve, reject) => {

      const { editId, data } = payload;

      apiService.put(`filemanager/update/${editId}`, data).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // return results to dispatcher without mutation //
        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [FilemanagerActions.REPLACE_ITEM](payload) {

    return new Promise((resolve, reject) => {

      const { editId, formData } = payload;

      apiService.post(`filemanager/replace/${editId}`, formData).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // return results to dispatcher without mutation //
        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [FilemanagerActions.DELETE_ITEM](editId) {

    return new Promise((resolve, reject) => {

      apiService.delete(`filemanager/delete/${editId}`).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          // pass full response so the view can react on "fileInUse" + usage list //
          return reject(data);
        }

        // refresh list in background//
        this.context.dispatch(FilemanagerActions.REFRESH_LIST);

        // return results to dispatcher without mutation //
        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject(response);

      });

    });

  };

  @Action
  [FilemanagerActions.FETCH_USAGE](fileId) {

    return new Promise((resolve, reject) => {

      apiService.get(`filemanager/usage/${fileId}`).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // return usage list to dispatcher without mutation //
        resolve(data.result?.usage || []);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [FilemanagerActions.UPLOAD_DATA](payload) {

    return new Promise((resolve, reject) => {

      const { editId, formData } = payload;

      apiService.post("filemanager/create", formData).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // return results to dispatcher without mutation //
        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

  @Action
  [FilemanagerActions.BATCH_UPLOAD](formData) {

    return new Promise((resolve, reject) => {

      apiService.post("filemanager/batch-create", formData).then(({ data }) => {

        const _verified = apiService.verifyApiResult(data);
        if (!_verified) {
          this.context.commit(Mutations.SET_ERROR, data);
          return reject();
        }

        // NOTE: list refresh is triggered once by the caller after all
        // sequential per-file uploads have finished (avoids N refreshes) //

        // return results to dispatcher without mutation //
        resolve(data.result);

      }).catch((response) => {

        this.context.commit(Mutations.SET_ERROR, response);
        reject();

      });

    });

  };

}
